"use client"

import { cn } from "@/lib/utils"

type Layer = "current" | "5yr" | "10yr"

interface LayerToggleProps {
  layers: Layer[]
  onChange: (layers: Layer[]) => void
}

// Line styles matching the chart exactly
const LAYER_CONFIG: {
  id: Layer
  label: string
  shortLabel: string // Compact label for mobile
  color: string // Hex color for SVG
  lineStyle: "solid" | "dashed" | "dotted"
}[] = [
  { id: "current", label: "Current Year", shortLabel: "Current", color: "#22c55e", lineStyle: "solid" },
  { id: "5yr", label: "5-Year Avg", shortLabel: "5-Year", color: "#78716c", lineStyle: "dashed" },
  { id: "10yr", label: "10-Year Avg", shortLabel: "10-Year", color: "#8b5cf6", lineStyle: "dotted" },
]

// SVG line icon that matches chart line styles
function LineIcon({
  color,
  lineStyle,
  inactive,
}: {
  color: string
  lineStyle: "solid" | "dashed" | "dotted"
  inactive?: boolean
}) {
  const strokeDasharray =
    lineStyle === "dashed" ? "4 2" : lineStyle === "dotted" ? "2 2" : "none"
  const strokeColor = color

  return (
    <svg width="20" height="12" viewBox="0 0 20 12" className="shrink-0">
      <line
        x1="0"
        y1="6"
        x2="20"
        y2="6"
        stroke={strokeColor}
        strokeWidth={lineStyle === "solid" ? 2.5 : 2}
        strokeDasharray={strokeDasharray}
        strokeLinecap="round"
      />
    </svg>
  )
}

export function LayerToggle({ layers, onChange }: LayerToggleProps) {
  const toggleLayer = (layerId: Layer) => {
    if (layers.includes(layerId)) {
      // Don't allow removing the last layer
      if (layers.length === 1) return
      onChange(layers.filter((l) => l !== layerId))
    } else {
      onChange([...layers, layerId])
    }
  }

  return (
    <div
      className="flex flex-nowrap items-center gap-1.5 sm:gap-2"
      role="group"
      aria-label="Chart layers"
    >
      {/* Hide "Show:" label on mobile to save space */}
      <span className="hidden sm:block text-sm text-gray-500 mr-0.5">Show:</span>
      {/* Independent toggle buttons — each layer is on/off on its own (all three
          can be shown at once), so they render as SEPARATE bordered pills with
          gaps rather than a joined segmented track (which would wrongly imply a
          single mutually-exclusive choice). The per-line color icon carries the
          chart's line style. */}
      {LAYER_CONFIG.map((layer) => {
        const isActive = layers.includes(layer.id)
        return (
          <button
            key={layer.id}
            type="button"
            onClick={() => toggleLayer(layer.id)}
            aria-pressed={isActive}
            className={cn(
              "inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-2.5 text-xs transition sm:px-3",
              isActive
                ? "border-green-600 bg-green-600 text-white"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            )}
          >
            <LineIcon
              color={isActive ? "#ffffff" : layer.color}
              lineStyle={layer.lineStyle}
              inactive={!isActive}
            />
            {/* Short labels on mobile, full labels on desktop */}
            <span className="sm:hidden">{layer.shortLabel}</span>
            <span className="hidden sm:inline">{layer.label}</span>
          </button>
        )
      })}
    </div>
  )
}
